import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getWallet, getTransactions } from '../api/loyalty.api';

const TYPE_CONFIG = {
    earned: { label: 'Earned', color: 'text-green-600', bg: 'bg-green-50', icon: 'ri-add-circle-line' },
    redeemed: { label: 'Redeemed', color: 'text-blue-600', bg: 'bg-blue-50', icon: 'ri-shopping-cart-line' },
    expired: { label: 'Expired', color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line' },
    manual_credit: { label: 'Credit', color: 'text-green-600', bg: 'bg-green-50', icon: 'ri-add-line' },
    manual_debit: { label: 'Debit', color: 'text-red-600', bg: 'bg-red-50', icon: 'ri-subtract-line' },
    voided: { label: 'Voided', color: 'text-gray-500', bg: 'bg-gray-50', icon: 'ri-close-circle-line' },
};

const STATUS_CONFIG = {
    pending: { label: 'Pending', color: 'text-amber-600', bg: 'bg-amber-50' },
    available: { label: 'Available', color: 'text-green-600', bg: 'bg-green-50' },
    used: { label: 'Used', color: 'text-blue-600', bg: 'bg-blue-50' },
    expired: { label: 'Expired', color: 'text-gray-500', bg: 'bg-gray-50' },
    voided: { label: 'Voided', color: 'text-gray-500', bg: 'bg-gray-50' },
};

export default function LoyaltyWallet() {
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [txnLoading, setTxnLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchWallet();
    }, []);

    useEffect(() => {
        fetchTransactions();
    }, [page, filter]);

    const fetchWallet = async () => {
        try {
            const res = await getWallet();
            if (res?.data?.isOk) {
                setWallet(res.data.data);
            }
        } catch (err) {
            console.error('Error fetching wallet:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactions = async () => {
        setTxnLoading(true);
        try {
            const params = { page, limit: 10 };
            if (filter) params.type = filter;
            const res = await getTransactions(params);
            if (res?.data?.isOk) {
                setTransactions(res.data.data || []);
                setPagination(res.data.pagination || { total: 0, page: 1, totalPages: 1 });
            }
        } catch (err) {
            console.error('Error fetching transactions:', err);
        } finally {
            setTxnLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!wallet?.isEnabled) {
        return (
            <div className="bg-card rounded-xl shadow-sm border border-border p-8 text-center">
                <i className="ri-gift-line text-4xl text-muted-foreground mb-3"></i>
                <h2 className="text-lg font-semibold text-foreground mb-2">Loyalty Program</h2>
                <p className="text-muted-foreground">The loyalty program is currently not available.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">Loyalty Wallet</h1>
                <Link to="/loyalty-program" className="text-sm text-primary font-medium flex items-center gap-1 hover:underline">
                    How it works <i className="ri-information-line"></i>
                </Link>
            </div>
            {/* Balance Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Available Points */}
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                            <i className="ri-coin-line text-xl text-primary"></i>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">Available Points</span>
                    </div>
                    <p className="text-3xl font-bold text-foreground">
                        {(wallet.available || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                        ≈ KES {(wallet.currencyValue || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Pending Points */}
                <div className="bg-card rounded-xl border border-border p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <i className="ri-timer-line text-xl text-amber-600"></i>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">Pending Points</span>
                    </div>
                    <p className="text-3xl font-bold text-foreground">
                        {(wallet.pending || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Awaiting delivery (not spendable yet)
                    </p>
                </div>

                {/* Expiring Soon */}
                <div className={`rounded-xl border p-5 ${
                    wallet.expiringSoon > 0
                        ? 'bg-red-50 border-red-200'
                        : 'bg-card border-border'
                }`}>
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            wallet.expiringSoon > 0 ? 'bg-red-100' : 'bg-muted'
                        }`}>
                            <i className={`ri-alarm-warning-line text-xl ${
                                wallet.expiringSoon > 0 ? 'text-red-500' : 'text-muted-foreground'
                            }`}></i>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">Expiring Soon</span>
                    </div>
                    <p className={`text-3xl font-bold ${
                        wallet.expiringSoon > 0 ? 'text-red-600' : 'text-foreground'
                    }`}>
                        {(wallet.expiringSoon || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Within next 30 days
                    </p>
                </div>
            </div>

            {/* Transaction History */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Transaction History</h2>
                    <select
                        value={filter}
                        onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-card text-foreground"
                    >
                        <option value="">All Types</option>
                        <option value="earned">Earned</option>
                        <option value="redeemed">Redeemed</option>
                        <option value="expired">Expired</option>
                        <option value="manual_credit">Manual Credit</option>
                        <option value="manual_debit">Manual Debit</option>
                    </select>
                </div>

                {txnLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <i className="ri-file-list-3-line text-3xl mb-2"></i>
                        <p>No transactions found</p>
                    </div>
                ) : (
                    <>
                        <div className="divide-y divide-border">
                            {transactions.map((txn) => {
                                const typeConf = TYPE_CONFIG[txn.type] || TYPE_CONFIG.voided;
                                const statusConf = STATUS_CONFIG[txn.status] || STATUS_CONFIG.voided;
                                const isPositive = txn.points > 0;

                                return (
                                    <div key={txn._id} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/50 transition-colors">
                                        {/* Icon */}
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConf.bg}`}>
                                            <i className={`${typeConf.icon} text-lg ${typeConf.color}`}></i>
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeConf.bg} ${typeConf.color}`}>
                                                    {typeConf.label}
                                                </span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${statusConf.bg} ${statusConf.color}`}>
                                                    {statusConf.label}
                                                </span>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1 truncate">
                                                {txn.orderNumber ? (
                                                    <Link to={`/account/orders/${txn.orderNumber}`} className="text-primary hover:underline font-medium">
                                                        Order #{txn.orderNumber}
                                                    </Link>
                                                ) : (
                                                    txn.reason || '—'
                                                )}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {new Date(txn.createdAt).toLocaleString('en-KE', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                                {txn.status === 'available' && txn.expiresAt && (
                                                    <> · Expires {new Date(txn.expiresAt).toLocaleDateString('en-KE', {
                                                        day: '2-digit', month: 'short', year: 'numeric',
                                                    })}</>
                                                )}
                                                {txn.status === 'pending' && (
                                                    <> · Awaiting delivery</>
                                                )}
                                                {txn.activatedAt && txn.status === 'available' && (
                                                    <> · Available since {new Date(txn.activatedAt).toLocaleDateString('en-KE', {
                                                        day: '2-digit', month: 'short', year: 'numeric',
                                                    })}</>
                                                )}
                                            </p>
                                        </div>

                                        {/* Points */}
                                        <div className="text-right flex-shrink-0">
                                            <span className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                                                {isPositive ? `+${txn.points}` : txn.points}
                                            </span>
                                            <p className="text-xs text-muted-foreground">
                                                Bal: {txn.balanceAfter?.toLocaleString() ?? '—'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                                <p className="text-sm text-muted-foreground">
                                    Showing page {pagination.page} of {pagination.totalPages}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPage(Math.max(1, page - 1))}
                                        disabled={page === 1}
                                        className="px-3 py-1 text-sm border border-border rounded-lg disabled:opacity-50 hover:bg-muted transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                                        disabled={page >= pagination.totalPages}
                                        className="px-3 py-1 text-sm border border-border rounded-lg disabled:opacity-50 hover:bg-muted transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
